import React, { useState, useEffect, useContext } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Grid,
  TextField,
  IconButton,
  Chip,
  CircularProgress,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { AutoTaskAuthContext } from "../../Context/AutoTaskContext";

const PRIMARY = "#6366f1";

const StyledQuillWrapper = styled(Box)(({ theme }) => ({
  "& .ql-toolbar.ql-snow": {
    border: "1px solid #e0e0e0",
    borderBottom: "none",
    backgroundColor: "#fafafa",
    borderRadius: "4px 4px 0 0",
    padding: "4px 8px",
  },
  "& .ql-container.ql-snow": {
    border: "1px solid #e0e0e0",
    borderRadius: "0 0 4px 4px",
    fontFamily: theme.typography.fontFamily,
  },
  "& .ql-editor": {
    minHeight: "160px",
    maxHeight: "220px",
    overflowY: "auto",
    fontSize: "13px",
    lineHeight: "1.5",
    padding: "10px 12px",
    "&.ql-blank::before": {
      color: "#9e9e9e",
      fontStyle: "italic",
      fontSize: "13px",
    },
  },
}));

const quillModules = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ["bold", "italic", "underline", "strike"],
    [{ list: "ordered" }, { list: "bullet" }],
    [{ color: [] }, { background: [] }],
    ["clean"],
  ],
};

const quillFormats = [
  "header",
  "bold",
  "italic",
  "underline",
  "strike",
  "list",
  "bullet",
  "color",
  "background",
];

const stripHtmlTags = (html) => {
  if (!html) return "";
  const tmp = document.createElement("DIV");
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || "";
};

const ItineraryViewDialog = ({
  open,
  onClose,
  itinerary,
  onUse,
  setSnackbar,
}) => {
  const { userInfo } = useContext(AutoTaskAuthContext) || {};
  const store_name = userInfo?.store_name || "Travel Agency";

  const [editableContent, setEditableContent] = useState("");
  const [editableName, setEditableName] = useState("");
  const [editableDescription, setEditableDescription] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    if (itinerary) {
      setEditableContent(itinerary.content || "");
      setEditableName(itinerary.name || "");
      setEditableDescription(itinerary.description || "");
    }
  }, [itinerary]);

  // ── COMPRESS IMAGE ─────────────────────────────────
  const compressImage = (src, maxW = 480, maxH = 340, quality = 0.78) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => {
        let w = img.naturalWidth || img.width;
        let h = img.naturalHeight || img.height;
        if (w > maxW) {
          h = Math.round((h * maxW) / w);
          w = maxW;
        }
        if (h > maxH) {
          w = Math.round((w * maxH) / h);
          h = maxH;
        }
        const cv = document.createElement("canvas");
        cv.width = w;
        cv.height = h;
        cv.getContext("2d").drawImage(img, 0, 0, w, h);
        resolve({ dataUrl: cv.toDataURL("image/jpeg", quality), w, h });
      };
      img.onerror = () => resolve(null);
      img.src = src;
    });
  };

  const generatePDF = async () => {
    if (!itinerary) return null;
    try {
      setIsGenerating(true);

      /* ═══════════════════════════════════════════════════════
         COLOUR PALETTE
      ═══════════════════════════════════════════════════════ */
      const C = {
        navy: [12, 28, 80],
        navyMid: [22, 52, 124],
        brand: [35, 85, 185],
        indigo: [99, 102, 241],
        indigoD: [68, 70, 190],
        gold: [210, 172, 50],
        goldL: [238, 208, 95],
        white: [255, 255, 255],
        offwhite: [247, 249, 253],
        iceBlue: [228, 237, 255],
        midBlue: [162, 190, 238],
        paleBlue: [237, 243, 255],
        dark: [18, 18, 32],
        mid: [80, 86, 108],
        muted: [142, 150, 172],
        divider: [202, 213, 232],
        shadow: [175, 192, 218],
        shadowD: [148, 165, 196],
      };

      /* ═══════════════════════════════════════════════════════
         PDF SETUP
      ═══════════════════════════════════════════════════════ */
      const pdf = new jsPDF("p", "pt", "a4");
      const PW = pdf.internal.pageSize.getWidth(); // 595.28
      const PH = pdf.internal.pageSize.getHeight(); // 841.89
      const ML = 48;
      const MR = 48;
      const MB = 58;
      const CW = PW - ML - MR; // 499.28

      let pageNum = 1;
      let cursorY = 0;

      /* ── drawing helpers ── */
      const fill = (x, y, w, h, col) => {
        pdf.setFillColor(...col);
        pdf.rect(x, y, w, h, "F");
      };
      const hline = (y, col = C.divider, lw = 0.5, x1 = ML, x2 = PW - MR) => {
        pdf.setDrawColor(...col);
        pdf.setLineWidth(lw);
        pdf.line(x1, y, x2, y);
      };
      const txt = (t, x, y, size, col, style = "normal", opts = {}) => {
        pdf.setFontSize(size);
        pdf.setFont("helvetica", style);
        pdf.setTextColor(...col);
        pdf.text(t, x, y, opts);
      };
      const dot = (x, y, r, col) => {
        pdf.setFillColor(...col);
        pdf.circle(x, y, r, "F");
      };

      /* ── footer ── */
      const drawFooter = () => {
        const FH = 46;
        const fy = PH - FH;
        fill(0, fy, PW, FH, C.navy);
        fill(0, fy, PW, 2.5, C.gold);
        fill(0, fy + 2.5, PW, 1, C.indigoD);

        const mid = PW / 2;
        [-16, 0, 16].forEach((offset) => {
          pdf.setFillColor(...C.gold);
          pdf.triangle(
            mid + offset - 4,
            fy + 24,
            mid + offset,
            fy + 18,
            mid + offset + 4,
            fy + 24,
            "F",
          );
          pdf.triangle(
            mid + offset - 4,
            fy + 24,
            mid + offset,
            fy + 30,
            mid + offset + 4,
            fy + 24,
            "F",
          );
        });

        txt(store_name.toUpperCase(), ML, fy + 30, 7, C.midBlue, "bold", {
          charSpace: 1.8,
        });
        txt(`Page ${pageNum}`, PW - MR, fy + 30, 7.5, C.midBlue, "normal", {
          align: "right",
        });
      };

      /* ── new page ── */
      const newPage = () => {
        drawFooter();
        pdf.addPage();
        pageNum++;
        fill(0, 0, PW, 7, C.navy);
        fill(0, 7, PW, 2.5, C.gold);
        fill(0, 9.5, PW, 1, C.indigoD);
        txt(store_name.toUpperCase(), ML, 20, 6.5, C.muted, "bold", {
          charSpace: 1.5,
        });
        txt(editableName, PW - MR, 20, 6.5, C.muted, "normal", {
          align: "right",
        });
        cursorY = 30;
      };

      const checkBreak = (h) => {
        if (cursorY + h > PH - MB) newPage();
      };

      /* ═══════════════════════════════════════════════════════
         PAGE 1 — HEADER  (reduced to 160pt, no eyebrow, no pills)
      ═══════════════════════════════════════════════════════ */
      const HDR_H = 120;

      // Base navy band
      fill(0, 0, PW, HDR_H, C.navy);

      // Right-side geometry
      pdf.setFillColor(...C.indigo);
      pdf.triangle(PW, 0, PW - 130, 0, PW, HDR_H, "F");
      pdf.setFillColor(...C.navyMid);
      pdf.triangle(PW, 0, PW - 60, 0, PW - 60, HDR_H, "F");
      pdf.setFillColor(...C.gold);
      pdf.triangle(PW, 0, PW - 42, 0, PW, 52, "F");
      pdf.setFillColor(...C.white);
      pdf.triangle(PW, 0, PW - 10, 0, PW, 18, "F");

      // Bottom edge accent lines
      fill(0, HDR_H, PW, 4, C.gold);
      fill(0, HDR_H + 4, PW, 1.5, C.indigo);
      fill(0, HDR_H + 5.5, PW, 0.5, C.divider);

      // Left vertical gold bar
      fill(0, 0, 5, HDR_H, C.gold);

      // ── STORE NAME — moved up since eyebrow is removed ──
      const storeLines = pdf.splitTextToSize(
        store_name.toUpperCase(),
        CW - 140,
      );
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(storeLines[0].length > 18 ? 24 : 30);
      pdf.setTextColor(...C.white);
      pdf.text(storeLines.slice(0, 2), ML + 10, 36, { charSpace: 1.2 });

      // Gold underline beneath store name
      const snWidth = Math.min(
        (pdf.getStringUnitWidth(storeLines[0]) *
          (storeLines[0].length > 18 ? 24 : 30)) /
          pdf.internal.scaleFactor,
        CW - 140,
      );
      fill(ML + 10, 44, snWidth, 2.5, C.gold);
      fill(ML + 10, 46.5, snWidth * 0.4, 1, C.goldL);

      // ── Itinerary title ──
      const titleLines = pdf.splitTextToSize(editableName, CW - 150);
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(titleLines.length > 1 ? 13 : 15);
      pdf.setTextColor(...C.iceBlue);
      pdf.text(titleLines.slice(0, 2), ML + 10, 62);

      // ── Description ──
      if (editableDescription) {
        pdf.setFont("helvetica", "normal");
        pdf.setFontSize(10);
        pdf.setTextColor(...C.midBlue);
        const dLines = pdf.splitTextToSize(editableDescription, CW - 150);
        const descY = titleLines.length > 1 ? 85 : 78;
        pdf.text(dLines.slice(0, 2), ML + 10, descY);
      }

      cursorY = HDR_H + 14;

      // ── Section divider after header ──
      hline(cursorY, C.divider, 0.4);
      cursorY += 10;
      txt("ITINERARY DETAILS", PW / 2, cursorY, 7.5, C.muted, "bold", {
        align: "center",
        charSpace: 2.5,
      });
      cursorY += 8;
      [PW / 2 - 10, PW / 2, PW / 2 + 10].forEach((dx) =>
        dot(dx, cursorY, 1.5, C.divider),
      );
      cursorY += 10;
      hline(cursorY, C.divider, 0.4);
      cursorY += 16;

      /* ═══════════════════════════════════════════════════════
         RICH TEXT CONTENT — slightly larger font sizes
      ═══════════════════════════════════════════════════════ */
      const CSS_W = 780;
      const el = document.createElement("div");
      el.style.cssText = `
        position:absolute;left:-9999px;top:0;
        width:${CSS_W}px;padding:28px 36px;
        background:#ffffff;box-sizing:border-box;
      `;
      el.innerHTML = `
        <style>
          *{box-sizing:border-box;margin:0;padding:0;}
          h1{
            font-size:22px;font-weight:800;color:#0c1c50;
            margin:0 0 14px;padding-bottom:8px;
            border-bottom:2.5px solid #d4af37;
            letter-spacing:0.3px;
            font-family:Arial,sans-serif;
          }
          h2{
            font-size:18px;font-weight:700;color:#1655b9;
            margin:24px 0 8px;padding:9px 14px 9px 14px;
            background:linear-gradient(90deg,#e4eeff 0%,#f7f9ff 100%);
            border-left:4px solid #6366f1;
            border-radius:0 5px 5px 0;
            font-family:Arial,sans-serif;
          }
          h3{
            font-size:16px;font-weight:700;color:#2233aa;
            margin:18px 0 5px;
            padding-left:10px;
            border-left:2px solid #d4af37;
            font-family:Arial,sans-serif;
          }
          p{
            font-size:15px;line-height:2;color:#12121e;
            margin:0 0 12px;font-family:Arial,sans-serif;
          }
          ul,ol{
            font-size:15px;line-height:2;color:#12121e;
            margin:0 0 12px;padding-left:24px;
            font-family:Arial,sans-serif;
          }
          li{margin-bottom:6px;}
          li::marker{color:#6366f1;font-weight:bold;}
          strong{color:#0c1c50;font-weight:700;}
          em{color:#363660;font-style:italic;}
          blockquote{
            border-left:4px solid #d4af37;
            margin:14px 0;padding:12px 18px;
            background:#fffdf0;color:#444;
            font-style:italic;font-size:15px;
            border-radius:0 6px 6px 0;
          }
          table{
            width:100%;border-collapse:collapse;
            margin:14px 0;font-size:14px;
            font-family:Arial,sans-serif;
          }
          th{
            background:#0c1c50;color:#fff;
            padding:8px 12px;text-align:left;font-size:13px;
          }
          td{
            padding:8px 12px;
            border-bottom:1px solid #dde8f8;color:#12121e;
          }
          tr:nth-child(even) td{background:#f3f7ff;}
        </style>
        <div>${editableContent || "<p>No content provided.</p>"}</div>
      `;
      document.body.appendChild(el);

      const textCanvas = await html2canvas(el, {
        scale: 2,
        useCORS: true,
        logging: false,
        allowTaint: true,
        backgroundColor: "#ffffff",
        imageTimeout: 0,
      });
      document.body.removeChild(el);

      const pxToPt = CW / CSS_W;
      const totalPt = (textCanvas.height / 2) * pxToPt;
      let renderedPt = 0;
      let remaining = totalPt;

      while (remaining > 0) {
        const avail = PH - cursorY - MB;
        const slicePt = Math.min(remaining, avail);
        const slicePx = Math.round((slicePt / pxToPt) * 2);
        const srcYPx = Math.round((renderedPt / pxToPt) * 2);

        const sc = document.createElement("canvas");
        sc.width = textCanvas.width;
        sc.height = Math.max(1, slicePx);
        const sctx = sc.getContext("2d");
        sctx.fillStyle = "#ffffff";
        sctx.fillRect(0, 0, sc.width, sc.height);
        sctx.drawImage(
          textCanvas,
          0,
          srcYPx,
          textCanvas.width,
          slicePx,
          0,
          0,
          textCanvas.width,
          slicePx,
        );

        pdf.addImage(
          sc.toDataURL("image/png"),
          "PNG",
          ML,
          cursorY,
          CW,
          slicePt,
        );
        cursorY += slicePt;
        renderedPt += slicePt;
        remaining -= slicePt;
        if (remaining > 0) newPage();
      }

      /* ═══════════════════════════════════════════════════════
         PHOTO GALLERY — 2 per row, no heading, same page as content
         Forces a page break only if fewer than 2 rows fit.
      ═══════════════════════════════════════════════════════ */
      if (itinerary.images && itinerary.images.length > 0) {
        const compressed = [];
        for (const img of itinerary.images) {
          const r = await compressImage(img.file, 480, 340, 0.78);
          if (r) compressed.push({ ...r, fileName: img.fileName });
        }

        if (compressed.length > 0) {
          const gap = 18;
          const cellW = (CW - gap) / 2;
          const cellH = Math.round(cellW * 0.66);
          const capH = 26;
          const rowH = cellH + capH + 18;

          // Add a small spacing gap before photos
          cursorY += 20;
          // Only break page if not even one row fits
          if (cursorY + rowH > PH - MB) newPage();

          // Thin ornamental rule before photos (no banner/heading)
          hline(cursorY, C.gold, 0.8);
          hline(cursorY + 3, C.divider, 0.3);
          cursorY += 14;

          for (let i = 0; i < compressed.length; i += 2) {
            // Only break if there's truly no room for this row
            if (cursorY + rowH > PH - MB) newPage();

            for (let j = 0; j < 2; j++) {
              const idx = i + j;
              if (idx >= compressed.length) break;
              const img = compressed[idx];
              const x = ML + j * (cellW + gap);
              const y = cursorY;

              // Triple-layer shadow
              fill(x + 5, y + 5, cellW, cellH + capH, C.shadowD);
              fill(x + 3, y + 3, cellW, cellH + capH, C.shadow);
              fill(x + 1, y + 1, cellW, cellH + capH, [215, 225, 240]);

              // Card white base
              fill(x, y, cellW, cellH + capH, C.white);

              // Image
              pdf.addImage(img.dataUrl, "JPEG", x, y, cellW, cellH);

              // Gold top accent
              fill(x, y, cellW, 4.5, C.gold);
              fill(x, y + 4.5, cellW, 1.5, C.goldL);

              // Card border
              pdf.setDrawColor(...C.divider);
              pdf.setLineWidth(0.6);
              pdf.rect(x, y, cellW, cellH + capH);

              // Caption bar
              fill(x, y + cellH, cellW, capH, C.paleBlue);
              fill(x, y + cellH, 3.5, capH, C.indigo);

              // Number badge
              fill(x + 9, y + cellH + 4, 18, 18, C.navy);
              txt(`${idx + 1}`, x + 18, y + cellH + 16, 8, C.white, "bold", {
                align: "center",
              });

              // Caption text
              if (img.fileName) {
                const cap =
                  img.fileName.length > 42
                    ? img.fileName.slice(0, 42) + "…"
                    : img.fileName;
                txt(cap, x + 32, y + cellH + 17, 8, C.mid, "normal");
              }
            }
            cursorY += rowH;
          }

          // Post-gallery rule
          cursorY += 6;
          hline(cursorY, C.gold, 0.8);
          hline(cursorY + 4, C.divider, 0.3);
          cursorY += 16;
        }
      }

      /* ═══════════════════════════════════════════════════════
         CLOSING BLOCK
      ═══════════════════════════════════════════════════════ */
      checkBreak(86);
      cursorY += 16;

      const cx = PW / 2;
      [cx - 28, cx - 14, cx, cx + 14, cx + 28].forEach((dx, i) => {
        const r = i === 2 ? 3 : 2;
        dot(dx, cursorY, r, i === 2 ? C.gold : C.divider);
      });
      cursorY += 14;

      fill(ML, cursorY, CW, 58, C.offwhite);
      fill(ML, cursorY, CW, 3, C.navy);
      fill(ML, cursorY + 55, CW, 3, C.navy);
      fill(ML, cursorY, 4, 58, C.gold);
      fill(ML + CW - 4, cursorY, 4, 58, C.gold);

      txt(
        "Thank you for choosing " + store_name,
        PW / 2,
        cursorY + 24,
        13,
        C.brand,
        "bold",
        { align: "center" },
      );

      const thankW = 140;
      fill(PW / 2 - thankW / 2, cursorY + 32, thankW, 1, C.gold);

      txt(
        "Wishing you an extraordinary journey ahead.",
        PW / 2,
        cursorY + 46,
        9.5,
        C.mid,
        "normal",
        { align: "center" },
      );
      cursorY += 70;

      drawFooter();

      const pdfBase64 = pdf.output("datauristring");
      setIsGenerating(false);
      return {
        base64: pdfBase64,
        fileName: `${editableName.replace(/\s+/g, "_")}_Itinerary.pdf`,
      };
    } catch (err) {
      console.error("PDF Error:", err);
      setIsGenerating(false);
      setSnackbar({
        open: true,
        message: "Failed to generate PDF. Please try again.",
        severity: "error",
      });
      return null;
    }
  };

  const handleUse = async () => {
    const pdfData = await generatePDF();
    if (pdfData) {
      const plainTextContent = stripHtmlTags(editableContent);
      onUse({
        name: editableName,
        content: plainTextContent,
        pdfBase64: pdfData.base64,
        pdfFileName: pdfData.fileName,
      });
      setSnackbar({
        open: true,
        message: "Itinerary added successfully!",
        severity: "success",
      });
      onClose();
    }
  };

  if (!itinerary) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{ sx: { maxHeight: isMobile ? "100%" : "90vh" } }}
    >
      <DialogTitle sx={{ py: 1.5, px: { xs: 1.5, sm: 2.5 } }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box
            display="flex"
            alignItems="center"
            gap={1}
            flexWrap="wrap"
            sx={{ minWidth: 0 }}
          >
            <Typography variant="subtitle1" fontWeight={700}>
              View & Edit Itinerary
            </Typography>
            <Chip
              icon={<CalendarTodayIcon sx={{ fontSize: 12 }} />}
              label={`${itinerary.total_days} ${
                itinerary.total_days === 1 ? "Day" : "Days"
              }`}
              size="small"
              sx={{
                height: 20,
                fontSize: "0.7rem",
                bgcolor: "#e3f2fd",
                color: "#1976d2",
              }}
            />
            {itinerary.images && itinerary.images.length > 0 && (
              <Chip
                label={`${itinerary.images.length} img${
                  itinerary.images.length !== 1 ? "s" : ""
                }`}
                size="small"
                sx={{
                  height: 20,
                  fontSize: "0.7rem",
                  bgcolor: "#f3e5f5",
                  color: "#9c27b0",
                }}
              />
            )}
          </Box>
          <IconButton size="small" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ py: 1.5, px: { xs: 1.5, sm: 2.5 } }}>
        <Grid container spacing={1.5}>
          <Grid item xs={12} sm={5}>
            <TextField
              fullWidth
              size="small"
              label="Itinerary Name"
              value={editableName}
              onChange={(e) => setEditableName(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={7}>
            <TextField
              fullWidth
              size="small"
              label="Description"
              value={editableDescription}
              onChange={(e) => setEditableDescription(e.target.value)}
            />
          </Grid>

          <Grid item xs={12}>
            <Typography
              variant="caption"
              fontWeight={700}
              sx={{ display: "block", mb: 0.5, color: "#555" }}
            >
              Itinerary Content
            </Typography>
            <StyledQuillWrapper>
              <ReactQuill
                theme="snow"
                value={editableContent}
                onChange={setEditableContent}
                modules={quillModules}
                formats={quillFormats}
                placeholder="Edit itinerary content..."
              />
            </StyledQuillWrapper>
          </Grid>

          {itinerary.images && itinerary.images.length > 0 && (
            <Grid item xs={12}>
              <Typography
                variant="caption"
                fontWeight={700}
                sx={{ display: "block", mb: 0.5, color: "#555" }}
              >
                Gallery ({itinerary.images.length} images)
              </Typography>
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                {itinerary.images.map((image, index) => (
                  <Box key={index}>
                    <Box
                      component="img"
                      src={image.file}
                      alt={image.fileName}
                      sx={{
                        width: 72,
                        height: 56,
                        objectFit: "cover",
                        borderRadius: 1,
                        border: "1px solid #e0e0e0",
                        display: "block",
                      }}
                    />
                  </Box>
                ))}
              </Box>
            </Grid>
          )}
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: { xs: 1.5, sm: 2.5 }, py: 1.5 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          size="small"
          disabled={isGenerating}
        >
          Cancel
        </Button>
        <Button
          onClick={handleUse}
          variant="contained"
          size="small"
          startIcon={
            isGenerating ? (
              <CircularProgress size={14} color="inherit" />
            ) : (
              <CheckCircleIcon />
            )
          }
          disabled={isGenerating}
          sx={{
            bgcolor: PRIMARY,
            "&:hover": { bgcolor: PRIMARY, opacity: 0.9 },
          }}
        >
          {isGenerating ? "Generating PDF..." : "Use Itinerary"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ItineraryViewDialog;
